/**
 * Framework-agnostic concurrent upload queue.
 *
 * - Runs up to `concurrency` processor calls in parallel.
 * - Retries failed jobs up to `maxRetries` times with a short backoff.
 * - Tracks per-job status and exposes a change listener so React hooks can
 *   mirror state without forcing a re-render inside the processor.
 */

export type UploadJobStatus =
  | 'queued'
  | 'uploading'
  | 'retrying'
  | 'done'
  | 'failed'
  | 'cancelled';

export interface UploadJob<T, R = unknown> {
  id: string;
  data: T;
  status: UploadJobStatus;
  retryCount: number;
  error?: Error;
  result?: R;
  progress?: number;
}

export interface UploadQueueOptions<T, R = unknown> {
  concurrency?: number;
  maxRetries?: number;
  /** Delay in ms before a retry. */
  retryDelayMs?: number;
  /**
   * Called for each job. Should throw on failure. The queue will retry up to
   * `maxRetries` times. `signal` is aborted when the job is cancelled.
   */
  processor: (data: T, signal: AbortSignal) => Promise<R>;
  /** Called whenever a job status changes. */
  onChange?: () => void;
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export class UploadQueue<T, R = unknown> {
  private jobs: UploadJob<T, R>[] = [];
  private running = 0;
  private concurrency: number;
  private maxRetries: number;
  private retryDelayMs: number;
  private processor: UploadQueueOptions<T, R>['processor'];
  private onChange?: () => void;
  private abortControllers = new Map<string, AbortController>();
  private paused = false;

  constructor(opts: UploadQueueOptions<T, R>) {
    this.concurrency = opts.concurrency ?? 3;
    this.maxRetries = opts.maxRetries ?? 2;
    this.retryDelayMs = opts.retryDelayMs ?? 1000;
    this.processor = opts.processor;
    this.onChange = opts.onChange;
  }

  getJobs(): readonly UploadJob<T, R>[] {
    return this.jobs;
  }

  add(data: T): string {
    const id = generateId();
    this.jobs.push({
      id,
      data,
      status: 'queued',
      retryCount: 0,
    });
    this.emit();
    this.processLoop();
    return id;
  }

  retry(id: string): boolean {
    const job = this.jobs.find((j) => j.id === id);
    if (!job || job.status !== 'failed') return false;
    job.status = 'queued';
    job.error = undefined;
    this.emit();
    this.processLoop();
    return true;
  }

  retryAll(): void {
    let changed = false;
    for (const job of this.jobs) {
      if (job.status === 'failed') {
        job.status = 'queued';
        job.error = undefined;
        changed = true;
      }
    }
    if (changed) {
      this.emit();
      this.processLoop();
    }
  }

  cancel(id: string): boolean {
    const ac = this.abortControllers.get(id);
    if (ac) {
      ac.abort();
      this.abortControllers.delete(id);
    }
    const job = this.jobs.find((j) => j.id === id);
    if (!job) return false;
    if (job.status === 'uploading' || job.status === 'retrying') {
      // The processor should observe the aborted signal and throw; we mark
      // cancelled once it finishes.
      return true;
    }
    if (job.status === 'queued') {
      job.status = 'cancelled';
      this.emit();
      return true;
    }
    return false;
  }

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    this.paused = false;
    this.processLoop();
  }

  isPaused(): boolean {
    return this.paused;
  }

  clearCompleted(): void {
    const before = this.jobs.length;
    this.jobs = this.jobs.filter((j) => j.status !== 'done' && j.status !== 'cancelled');
    if (this.jobs.length !== before) this.emit();
  }

  private emit() {
    this.onChange?.();
  }

  private async processLoop() {
    if (this.paused) return;
    while (this.running < this.concurrency) {
      const job = this.jobs.find(
        (j) => j.status === 'queued' || j.status === 'retrying'
      );
      if (!job) break;
      this.runJob(job);
    }
  }

  private async runJob(job: UploadJob<T, R>) {
    this.running++;
    job.status = 'uploading';
    this.emit();

    const ac = new AbortController();
    this.abortControllers.set(job.id, ac);

    try {
      const result = await this.processor(job.data, ac.signal);
      job.result = result;
      job.status = 'done';
    } catch (err) {
      if (ac.signal.aborted) {
        job.status = 'cancelled';
      } else {
        job.retryCount += 1;
        const error = err instanceof Error ? err : new Error(String(err));
        job.error = error;
        if (job.retryCount <= this.maxRetries) {
          job.status = 'retrying';
          // Retry after a short delay without holding a concurrency slot.
          this.running--;
          this.emit();
          await sleep(this.retryDelayMs);
          this.processLoop();
          return;
        }
        job.status = 'failed';
      }
    } finally {
      this.abortControllers.delete(job.id);
      this.running--;
      this.emit();
      this.processLoop();
    }
  }
}
